import { translateBackend } from '@assets/i18n/config';
import type { AlertBanner } from '@generated/graphql';

import type { SxProps } from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';

import { Box, Button, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { BANNER_TYPE_OPTIONS } from '../constants';

const AlertBannerDisplay = ({
  bannerFields,
  onDismiss,
  onAction,
  sx,
}: {
  bannerFields: Partial<AlertBanner>;
  onDismiss?: () => void;
  onAction?: () => void;
  sx?: SxProps;
}) => {
  const theme = useTheme();

  const isDismissible = bannerFields.configuration?.makeDismissible ?? false;
  const hasAction = bannerFields.configuration?.enableAction ?? false;
  const actionLabel = bannerFields.actionButton?.label || '';

  const config = BANNER_TYPE_OPTIONS.find(
    (option) => option.key === bannerFields.bannerType,
  );

  if (!config) return null;

  const iconColor = theme.palette[config.iconColor];

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          p: 1,
          bgcolor: theme.palette[config.backgroundColor],
          borderRadius: theme.borderRadius.sm,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Icon icon={config.icon} sx={{ color: iconColor }} />

        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {translateBackend(bannerFields.bannerCopy)}
        </Typography>
      </Box>

      {(hasAction || isDismissible) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {hasAction && actionLabel && (
            <Button
              variant='outlined'
              size='small'
              onClick={onAction}
              sx={{
                borderColor: `${iconColor} !important`,
                '&:hover': {
                  borderColor: `${iconColor} !important`,
                  backgroundColor: `${iconColor}14 !important`,
                },
              }}
            >
              {actionLabel}
            </Button>
          )}

          {isDismissible && (
            <IconButton
              aria-label='close'
              size='xsmall'
              onClick={onDismiss}
              sx={{ color: iconColor }}
            >
              <CloseIcon fontSize='inherit' />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AlertBannerDisplay;
